import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ATTENDANCE_STATUSES,
  ATTENDANCE_STATUS_LABEL,
  ENROLLMENT_STATUS_LABEL,
  SESSION_STATUS_LABEL,
  type AttendanceStatus,
} from '@doit/shared';
import { ArrowLeft, Check, Lock, Loader2, TriangleAlert } from 'lucide-react';
import clsx from 'clsx';
import { Alert, Button, Card, EmptyState, FullPageLoader, PageHeader } from '../../components/ui';
import { Badge } from '../../components/badges';
import { useToast } from '../../components/Toast';
import { ApiError } from '../../api/client';
import { useCurrentUser } from '../../api/auth';
import { useMarkOne, useSaveAttendance, useSessionRoster, useUpdateSession } from '../../api/queries';
import type { RosterRow } from '../../api/types';
import { formatDateLong, formatDateTime, formatPercent } from '../../lib/format';

/**
 * სტატუსის პალიტრა — ერთი ფერი სამივე ადგილას:
 * სვეტის სათაური, მოსანიშნი რგოლი და ჯამური ბეიჯი.
 */
const STATUS_STYLE: Record<
  AttendanceStatus,
  { active: string; idle: string; head: string; quick: string }
> = {
  PRESENT: {
    active: 'bg-ok border-ok text-white shadow-sm',
    idle: 'border-line-strong bg-surface hover:border-ok hover:bg-ok/10',
    head: 'text-ok',
    quick: 'border-line-strong text-ink-soft hover:border-ok hover:text-ok',
  },
  ABSENT: {
    active: 'bg-danger border-danger text-white shadow-sm',
    idle: 'border-line-strong bg-surface hover:border-danger hover:bg-danger/10',
    head: 'text-danger',
    quick: 'border-line-strong text-ink-soft hover:border-danger hover:text-danger',
  },
};

/** ერთი სვეტის სიგანე — სათაური და რგოლები ზუსტად უნდა დაემთხვეს */
const COL = 'w-[84px] shrink-0 text-center';

export function SessionJournalPage() {
  const { sessionId = '' } = useParams();
  const toast = useToast();
  const { data, isLoading, error } = useSessionRoster(sessionId);
  const save = useSaveAttendance(sessionId, data?.session.groupId);
  const markOne = useMarkOne(sessionId, data?.session.groupId);
  const updateSession = useUpdateSession(data?.session.groupId);

  const [marks, setMarks] = useState<Record<string, AttendanceStatus>>({});
  const user = useCurrentUser();
  const backLabel = user.role === 'LECTURER' ? 'აღრიცხვა' : 'ჟურნალი';

  const [topic, setTopic] = useState('');
  /** რომელი სტრიქონი იწერება ახლა — ვიზუალური დადასტურებისთვის */
  const [saving, setSaving] = useState<Set<string>>(new Set());
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const topicRef = useRef('');

  // სერვერის მდგომარეობა → ლოკალური ფორმა
  useEffect(() => {
    if (!data) return;
    const initial: Record<string, AttendanceStatus> = {};
    for (const row of data.roster) if (row.status) initial[row.enrollmentId] = row.status;
    setMarks(initial);
    setTopic(data.session.topic ?? '');
    topicRef.current = data.session.topic ?? '';
  }, [data]);

  const active = useMemo(
    () => (data?.roster ?? []).filter((r) => r.enrollmentStatus === 'ACTIVE'),
    [data],
  );
  const inactive = useMemo(
    () => (data?.roster ?? []).filter((r) => r.enrollmentStatus !== 'ACTIVE'),
    [data],
  );

  const counts = useMemo(() => {
    const c = { PRESENT: 0, ABSENT: 0, unmarked: 0 };
    for (const row of active) {
      const status = marks[row.enrollmentId];
      if (status) c[status]++;
      else c.unmarked++;
    }
    return c;
  }, [active, marks]);

  if (isLoading) return <FullPageLoader />;

  if (error || !data) {
    return (
      <div className="rounded-[10px] border border-line bg-surface shadow-xs">
        <EmptyState
          title="გაკვეთილი მიუწვდომელია"
          description={error instanceof ApiError ? error.message : 'გაკვეთილი ვერ მოიძებნა'}
          action={
            <Link to="/journal">
              <Button variant="secondary">უკან</Button>
            </Link>
          }
        />
      </div>
    );
  }

  const { session, gate } = data;

  const cancelled = session.status === 'CANCELLED';
  // საათობრივი ფანჯარა აღარ არსებობს — გადამწყვეტია მხოლოდ, მიმდინარე გაკვეთილია თუ არა
  const canMark = cancelled ? false : gate.canMark;
  const reason = cancelled ? 'გაკვეთილი გაუქმებულია — დასწრება არ აღირიცხება' : gate.reason;

  /** თემა ინახება დადასტურებაზე — სწორედ ის ხსნის აღრიცხვას */
  async function saveTopic() {
    const value = topic.trim();
    if (!value) {
      toast.error('თემა ცარიელია');
      return;
    }
    try {
      const res = await updateSession.mutateAsync({ id: sessionId, data: { topic: value } });
      topicRef.current = value;
      setSavedAt(new Date());

      // ავტომატური მონიშვნა ჩუმად რომ მოხდეს, ლექტორი ვერ მიხვდებოდა,
      // რომ სია უკვე შევსებულია და დამსწრეები მან უნდა მონიშნოს
      if (res.autoMarked && res.autoMarked > 0) {
        toast.success(
          `თემა დადასტურდა — ${res.autoMarked} სტუდენტი მოინიშნა გაცდენილად. დამსწრეები მონიშნე.`,
        );
      } else {
        toast.success('თემა დადასტურდა — აღრიცხვა გაიხსნა');
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'თემა ვერ შეინახა');
    }
  }

  /** დაუდასტურებელი ცვლილება — ღილაკი მხოლოდ მაშინაა აქტიური */
  const topicDirty = topic.trim() !== topicRef.current.trim();

  /**
   * ერთი მონიშვნა — მაშინვე იწერება, შენახვის ღილაკის გარეშე.
   * იმავე სტატუსზე ხელახალი დაჭერა მონიშვნას ხსნის (გადამრთველი).
   */
  async function setStatus(enrollmentId: string, status: AttendanceStatus) {
    const previous = marks[enrollmentId];
    const clearing = previous === status;

    setMarks((prev) => {
      const next = { ...prev };
      if (clearing) delete next[enrollmentId];
      else next[enrollmentId] = status;
      return next;
    });
    setSaving((prev) => new Set(prev).add(enrollmentId));

    try {
      // markOne სერვერზე წყვეტს, დასვას თუ მოხსნას — ორ მოთხოვნას შორის
      // მდგომარეობა რომ არ გაიყოს
      await markOne.mutateAsync({ enrollmentId, status });
      setSavedAt(new Date());
    } catch (err) {
      // ჩავარდნაზე ვბრუნდებით — ეკრანი ბაზას არ უნდა ეშველებოდეს
      setMarks((prev) => {
        const next = { ...prev };
        if (previous) next[enrollmentId] = previous;
        else delete next[enrollmentId];
        return next;
      });
      toast.error(err instanceof ApiError ? err.message : 'ვერ შეინახა');
    } finally {
      setSaving((prev) => {
        const next = new Set(prev);
        next.delete(enrollmentId);
        return next;
      });
    }
  }

  /** ყველას ერთად მონიშვნა — ერთი მოთხოვნით */
  async function markAll(status: AttendanceStatus) {
    const targets = active.filter((r) => marks[r.enrollmentId] !== status);
    if (targets.length === 0) return;

    const previous = { ...marks };
    setMarks((prev) => {
      const next = { ...prev };
      for (const r of targets) next[r.enrollmentId] = status;
      return next;
    });
    setSaving(new Set(targets.map((r) => r.enrollmentId)));

    try {
      await save.mutateAsync({
        marks: targets.map((r) => ({ enrollmentId: r.enrollmentId, status })),
      });
      setSavedAt(new Date());
    } catch (err) {
      setMarks(previous);
      toast.error(err instanceof ApiError ? err.message : 'ვერ შეინახა');
    } finally {
      setSaving(new Set());
    }
  }

  const locked = !canMark;

  return (
    <>
      <Link
        to="/journal"
        className="mb-4 inline-flex items-center gap-1.5 text-[13px] text-ink-mute transition hover:text-ink"
      >
        <ArrowLeft className="size-4" />
        {backLabel}
      </Link>

      <PageHeader
        title={`${session.group.name} · გაკვეთილი ${session.orderNo}`}
        subtitle={`${formatDateLong(session.date)} · ${session.startTime}${
          session.endTime ? `–${session.endTime}` : ''
        } · ${session.group.course.name}`}
        action={
          <span className="flex items-center gap-2 text-[12px] text-ink-mute">
            {saving.size > 0 ? (
              <>
                <Loader2 className="size-3.5 animate-spin" />
                ინახება…
              </>
            ) : savedAt ? (
              <>
                <Check className="size-3.5 text-ok" />
                შენახულია {formatDateTime(savedAt.toISOString()).split(', ')[1]}
              </>
            ) : null}
          </span>
        }
      />

      {/* --- ჩაკეტვის მიზეზი --- */}
      {locked && (
        <div className="mb-4">
          <Alert kind={gate.needsTopic ? 'info' : 'error'}>
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium">
              <Lock className="size-3.5 shrink-0" />
              {reason}
            </span>
          </Alert>
        </div>
      )}

      {!locked && gate.bypassed && gate.isPast && (
        <div className="mb-4">
          <Alert kind="info">
            გასული გაკვეთილია — ლექტორისთვის დახურულია, შენ ისტორიას ასწორებ.
          </Alert>
        </div>
      )}

      {/* --- თემა: აღრიცხვის წინაპირობა --- */}
      <Card
        title="გაკვეთილის თემა"
        className="mb-5"
        action={
          gate.needsTopic ? (
            <Badge tone="warn">სავალდებულო</Badge>
          ) : (
            <Badge tone="ok">
              <Check className="mr-0.5 inline size-2.5" />
              შევსებულია
            </Badge>
          )
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && topicDirty) void saveTopic();
            }}
            placeholder="გაკვეთილის თემა"
            className="min-w-0 flex-1 rounded-lg border border-line-strong bg-surface px-3 py-2.5 text-sm transition placeholder:text-ink-mute focus:border-brand-600 focus:outline-none focus:ring-3 focus:ring-brand-100"
          />
          <Button
            onClick={() => void saveTopic()}
            disabled={!topicDirty || !topic.trim()}
            loading={updateSession.isPending}
            icon={<Check className="size-4" />}
          >
            დადასტურება
          </Button>
        </div>
        {gate.needsTopic && user.role !== 'LECTURER' && (
          <p className="mt-1.5 text-[12px] text-ink-mute">
            თემის ჩაწერის შემდეგ გაიხსნება დასწრების აღრიცხვა.
          </p>
        )}
      </Card>

      {/* --- სწრაფი მოქმედებები --- */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <span className="text-[12px] font-medium text-ink-mute">ყველას მონიშვნა:</span>
        {ATTENDANCE_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => void markAll(s)}
            disabled={locked || saving.size > 0}
            className={clsx(
              'rounded-lg border px-2.5 py-1 text-[12px] font-medium transition disabled:cursor-not-allowed disabled:opacity-40',
              STATUS_STYLE[s].quick,
            )}
          >
            {ATTENDANCE_STATUS_LABEL[s]}
          </button>
        ))}

        <span className="ml-auto flex flex-wrap items-center gap-2 text-[12px]">
          <Badge tone="ok">{ATTENDANCE_STATUS_LABEL.PRESENT} {counts.PRESENT}</Badge>
          <Badge tone="danger">{ATTENDANCE_STATUS_LABEL.ABSENT} {counts.ABSENT}</Badge>
          {counts.unmarked > 0 && <Badge>მოსანიშნი {counts.unmarked}</Badge>}
        </span>
      </div>

      {/* --- სია --- */}
      <div
        className={clsx(
          'overflow-hidden rounded-[10px] border border-line bg-surface shadow-xs transition',
          locked && 'opacity-60',
        )}
      >
        {active.length === 0 ? (
          <EmptyState
            title="ჯგუფში აქტიური სტუდენტები არ არიან"
            description="მენეჯერმა ჯერ უნდა ჩარიცხოს სტუდენტები."
          />
        ) : (
          <>
            {/* --- სვეტების სათაური: სიტყვა ერთხელ, ზემოთ --- */}
            <div className="flex items-center gap-x-4 border-b border-line bg-canvas/70 px-4 py-2">
              <span className="min-w-0 flex-1 text-[11px] font-semibold uppercase tracking-wide text-ink-mute">
                სტუდენტი
              </span>
              <div className="flex gap-1.5">
                {ATTENDANCE_STATUSES.map((st) => (
                  <span
                    key={st}
                    className={clsx(
                      COL,
                      'text-[11px] font-semibold uppercase tracking-wide',
                      STATUS_STYLE[st].head,
                    )}
                  >
                    {ATTENDANCE_STATUS_LABEL[st]}
                  </span>
                ))}
              </div>
            </div>

            <ul className="divide-y divide-line">
            {active.map((row) => (
              <RosterItem
                key={row.enrollmentId}
                row={row}
                value={marks[row.enrollmentId]}
                saving={saving.has(row.enrollmentId)}
                disabled={locked}
                onChange={(s) => void setStatus(row.enrollmentId, s)}
              />
            ))}
            </ul>
          </>
        )}

        {inactive.length > 0 && user.role !== 'LECTURER' && (
          <div className="border-t border-line bg-canvas/50 px-4 py-3">
            <p className="mb-2 text-[12px] font-medium text-ink-mute">
              არააქტიური ჩარიცხვები ({inactive.length}) — დასწრება არ აღირიცხება
            </p>
            <ul className="space-y-1">
              {inactive.map((row) => (
                <li key={row.enrollmentId} className="flex items-center gap-2 text-[13px]">
                  <span className="text-ink-mute line-through">{row.fullName}</span>
                  <Badge>{ENROLLMENT_STATUS_LABEL[row.enrollmentStatus]}</Badge>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {user.role !== 'LECTURER' && (
        <p className="mt-4 text-[12px] text-ink-mute">
          გაკვეთილის სტატუსი: {SESSION_STATUS_LABEL[session.status]} · პირველივე მონიშვნაზე
          ხდება „ჩატარებული".
        </p>
      )}
    </>
  );
}

// ============================================================

function RosterItem({
  row,
  value,
  saving,
  disabled,
  onChange,
}: {
  row: RosterRow;
  value: AttendanceStatus | undefined;
  saving: boolean;
  disabled: boolean;
  onChange: (status: AttendanceStatus) => void;
}) {
  const atRisk = row.pct !== null && row.pct < 75;

  return (
    <li
      className={clsx(
        'flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 transition',
        saving ? 'bg-brand-50/60' : 'hover:bg-canvas/50',
      )}
    >
      <div className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <Link
            to={`/students/${row.studentId}`}
            className="text-[13px] font-medium text-ink hover:text-brand-700 hover:underline"
          >
            {row.fullName}
          </Link>
          {saving && <Loader2 className="size-3 animate-spin text-brand-600" />}
        </span>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[12px]">
          <span className={clsx('tabular', atRisk ? 'font-medium text-danger' : 'text-ink-mute')}>
            {formatPercent(row.pct, 0, 'ჯერ არ არის')}
          </span>
          {row.streak >= 3 && (
            <Badge tone="danger">
              <TriangleAlert className="mr-0.5 inline size-3" />
              {row.streak} ზედიზედ
            </Badge>
          )}
        </div>
      </div>

      <div className="flex gap-1.5">
        {ATTENDANCE_STATUSES.map((s) => {
          const isActive = value === s;
          return (
            <span key={s} className={clsx(COL, 'flex justify-center')}>
              <button
                onClick={() => onChange(s)}
                disabled={disabled}
                aria-pressed={isActive}
                aria-label={ATTENDANCE_STATUS_LABEL[s]}
                title={ATTENDANCE_STATUS_LABEL[s]}
                className={clsx(
                  'grid size-7 place-items-center rounded-full border-2 transition disabled:cursor-not-allowed disabled:opacity-50',
                  isActive ? STATUS_STYLE[s].active : STATUS_STYLE[s].idle,
                )}
              >
                {isActive && <Check className="size-4" strokeWidth={3} />}
              </button>
            </span>
          );
        })}
      </div>
    </li>
  );
}
